import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, AlertCircle } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import PasswordField from "@/components/auth/PasswordField";
import SocialButtons from "@/components/auth/SocialButtons";
import TwoFactorCodeForm from "@/components/auth/TwoFactorCodeForm";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { validateEmail } from "@/lib/validation";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verify2fa } = useAuth();
  const from = location.state?.from || null;
  const [form, setForm] = useState({ email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA challenge state — set when the backend asks for a TOTP code.
  const [twofa, setTwofa] = useState(null); // { token } or null

  // Google OAuth bounces back here with ?error=<code> when the flow fails.
  const oauthError = new URLSearchParams(location.search).get("error");
  const OAUTH_MESSAGES = {
    google: "Google sign-in failed. Please try again.",
    google_state: "Your sign-in link expired. Please try again.",
    google_email: "Google didn't share a verified email for that account.",
    disabled: "That account is disabled. Contact your admin.",
    // The callback is a browser redirect, so the operator's own maintenance
    // message can't ride along in a response body — this stands in for it.
    maintenance: "We're down for maintenance right now. Please try again shortly.",
  };
  const oauthMessage = oauthError ? OAUTH_MESSAGES[oauthError] || OAUTH_MESSAGES.google : null;

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    const next = {};
    const emailError = validateEmail(form.email);
    if (emailError) next.email = emailError;
    // 8, matching signup / reset / accept-invite / change-password. At 6 this
    // told someone with a valid 8-character password they needed "at least 6".
    if (form.password.length < 8) next.password = "At least 8 characters.";
    setErrors(next);
    if (Object.keys(next).length) return;

    // Calls POST /auth/login; falls back to a demo session if the backend
    // is offline. On success, continue to where the user was headed.
    setLoading(true);
    try {
      const result = await login(form.email.trim(), form.password, { remember });
      if (result?.twofaRequired) {
        setTwofa({ token: result.twofaToken });   // show the code step
        return;
      }
      // Honour an explicit protected-route redirect; otherwise route by role.
      const role = result?.user?.role || "tester";
      navigate(from || homeForRole(role), { replace: true });
    } catch (err) {
      setFormError(err.message || "Sign in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Accepts a TOTP code or a backup code — the server takes either at this step.
  // Errors propagate so TwoFactorCodeForm can show them and clear the input.
  const submit2fa = async (code) => {
    const result = await verify2fa(twofa.token, code, { remember });
    const role = result?.user?.role || "tester";
    navigate(from || homeForRole(role), { replace: true });
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to run tests and review your results."
    >
      {oauthMessage && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
          {oauthMessage}
        </div>
      )}

      {twofa ? (
        <TwoFactorCodeForm
          onSubmit={submit2fa}
          onCancel={() => { setTwofa(null); setFormError(""); }}
        />
      ) : (
      <>
      <SocialButtons next={from} />

      <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
        <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
        or continue with email
        <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {formError && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300"
          >
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <span>{formError}</span>
          </div>
        )}
        <Input
          id="email"
          type="email"
          label="Email"
          icon={Mail}
          autoComplete="email"
          placeholder="you@company.com"
          value={form.email}
          onChange={update("email")}
          error={errors.email}
        />
        <PasswordField
          value={form.password}
          onChange={update("password")}
          error={errors.password}
          autoComplete="current-password"
          placeholder="••••••••"
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Remember me
          </label>
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400"
          >
            Forgot password?
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" /> Signing in…
            </>
          ) : (
            <>
              Sign in <ArrowRight size={18} />
            </>
          )}
        </Button>
      </form>
      </>
      )}

      <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
        New here?{" "}
        <Link
          to="/signup"
          className="font-semibold text-brand-600 hover:text-brand-500 dark:text-brand-400"
        >
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}
